import { useWorkspaces } from "@/api/account";
import { Workspace } from "@/models/account";
import useLocalStorage from "@/hooks/use-local-storage";
import { useMemo, useEffect, useCallback } from "react";

export const useWorkspace = () => {
  const { data: workspaces, isLoading, isError } = useWorkspaces();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useLocalStorage<
    string | null
  >("selectedWorkspaceId");

  const currentWorkspace = useMemo(() => {
    if (!workspaces || workspaces.length === 0) return null;

    if (selectedWorkspaceId) {
      const found = workspaces.find((w) => w.id === selectedWorkspaceId);
      if (found) return found;
    }

    return workspaces[0];
  }, [workspaces, selectedWorkspaceId]);

  useEffect(() => {
    if (!isLoading && workspaces) {
      if (workspaces.length === 0) {
        if (selectedWorkspaceId !== null) {
          setSelectedWorkspaceId(null);
        }
      } else if (selectedWorkspaceId === undefined || selectedWorkspaceId === null) {
        setSelectedWorkspaceId(workspaces[0].id);
      } else {
        const found = workspaces.find((w) => w.id === selectedWorkspaceId);
        if (!found) {
          setSelectedWorkspaceId(workspaces[0].id);
        }
      }
    }
  }, [isLoading, selectedWorkspaceId, setSelectedWorkspaceId, workspaces]);

  // Query key scoping (workspace ID in every query key via useSyncExternalStore)
  // handles cache isolation automatically — no need to cancel/invalidate/reset.
  // Switching workspace changes the key, TanStack Query fetches if not cached,
  // and old workspace data stays in its own cache entry for instant switch-back.
  const selectWorkspace = useCallback((workspaceId: string) => {
    if (workspaceId === selectedWorkspaceId) return;
    setSelectedWorkspaceId(workspaceId);
  }, [selectedWorkspaceId, setSelectedWorkspaceId]);

  const hasWorkspace = !isLoading && workspaces && workspaces.length > 0;

  return {
    workspaces: workspaces || [],
    currentWorkspace,
    selectWorkspace,
    isLoading,
    isError,
    hasWorkspace,
  };
};
